import { useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { Box, Button, IconButton, Menu, MenuItem, Popover, Slider, Tooltip, Typography, alpha } from '@mui/material'
import {
  CalendarMonth,
  FastForward,
  FastRewind,
  Pause,
  PlayArrow,
  SkipNext,
  SkipPrevious,
} from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import GraphCell from '../GraphCell'
import { CLOCK_THEMES } from './MapClock'
import { glassSx, overlayTextShadow } from './overlayStyles'

const ACCENT = CLOCK_THEMES.replay.accent   // amber: everything on the Review page reads as replay
const SPEEDS = [1, 2, 4, 8, 16, 32]
const STEP_MS = 60 * 1000   // ⏪ / ⏩ jump one minute
const BUCKET_MS = 10 * 60 * 1000   // the server counts targets per 10 minutes

const formatRange = (ms) => {
  const min = Math.round(ms / 60000)
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h < 48) return m ? `${h} h ${m} min` : `${h} h`
  return `${Math.round(h / 24)} days`
}

// Dark field for the v8 date pickers (they ignore renderInput; style goes through slotProps)
const pickerFieldSx = {
  width: 210,
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiPickersInputBase-root, & .MuiInputBase-root': {
    color: '#fff',
    fontSize: '0.85rem',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: '8px',
  },
  '& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.18)',
  },
  '& .MuiPickersInputBase-root:hover .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.35)',
  },
  '& .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: ACCENT,
  },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
}

const smallButtonSx = {
  width: 30,
  height: 30,
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
}

const darkPaperSx = {
  mt: -1,
  color: '#fff',
  backgroundColor: 'rgba(12, 16, 20, 0.95)',
  border: `1px solid ${alpha(ACCENT, 0.3)}`,
  borderRadius: '10px',
  boxShadow: '0 10px 36px rgba(0,0,0,0.55)',
}

// Replay command panel for the Review page, kept to one slim row so it covers little of the map:
// transport buttons · timeline (activity graph behind it) · speed menu · date range pop-up.
// The current replay time itself is shown by the REPLAY clock at the top left.
const ReplayControls = ({
  startDate,
  endDate,
  currentTime,
  isPlaying,
  playSpeed,
  targetCounts,
  onStartChange,
  onEndChange,
  onPlayPause,
  onSpeed,
  onScrub,     // while dragging the slider
  onSeek,      // jump (slider released, buttons)
}) => {
  const [speedAnchor, setSpeedAnchor] = useState(null)
  const [rangeAnchor, setRangeAnchor] = useState(null)

  // Hover readout for the activity graph (the graph itself sits under the slider and gets no
  // mouse events, so its own tooltip can't show)
  const timelineRef = useRef(null)
  const [hover, setHover] = useState(null)   // { x, time, from, count }
  // While dragging, the slider's own time bubble is shown instead of the hover label
  const [dragging, setDragging] = useState(false)

  const buckets = useMemo(
    () => (targetCounts || []).map((b) => ({ t: new Date(b.log_time).getTime(), count: b.speed || 0 })),
    [targetCounts]
  )

  const start = startDate.valueOf()
  const end = endDate.valueOf()
  const now = currentTime.valueOf()

  const atStart = now <= start
  const atEnd = now >= end

  const handleTimelineMove = (e) => {
    const el = timelineRef.current
    if (!el || end <= start) return
    const rect = el.getBoundingClientRect()
    const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width)
    const time = start + (x / rect.width) * (end - start)

    // Last bucket that starts at or before this time
    let bucket = null
    for (let i = buckets.length - 1; i >= 0; i--) {
      if (buckets[i].t <= time) { bucket = buckets[i]; break }
    }
    const from = bucket ? bucket.t : Math.floor(time / BUCKET_MS) * BUCKET_MS
    setHover({ x, width: rect.width, time, from, count: bucket ? bucket.count : 0 })
  }

  const popperProps = {
    container: document.body,
    modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }],
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(720px, calc(100% - 32px))',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: '12px',
        color: '#fff',
        // Transparent over the map; darker while the pointer is on it, so controls stay easy to read
        ...glassSx(ACCENT, { interactive: true }),
      }}
    >
      {/* Transport */}
      <Box display="flex" alignItems="center" flexShrink={0}>
        <Tooltip title="Back to start" arrow>
          <span>
            <IconButton size="small" onClick={() => onSeek(startDate)} disabled={atStart} sx={smallButtonSx}>
              <SkipPrevious fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Back 1 minute" arrow>
          <span>
            <IconButton size="small" onClick={() => onSeek(currentTime.subtract(STEP_MS, 'ms'))} disabled={atStart} sx={smallButtonSx}>
              <FastRewind fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={isPlaying ? 'Pause' : atEnd ? 'Replay from start' : 'Play'} arrow>
          <IconButton
            onClick={onPlayPause}
            sx={{
              mx: 0.25,
              width: 36,
              height: 36,
              color: '#1a1205',
              backgroundColor: ACCENT,
              boxShadow: `0 0 12px ${alpha(ACCENT, 0.4)}`,
              '&:hover': { backgroundColor: '#FFC34D' },
            }}
          >
            {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Forward 1 minute" arrow>
          <span>
            <IconButton size="small" onClick={() => onSeek(currentTime.add(STEP_MS, 'ms'))} disabled={atEnd} sx={smallButtonSx}>
              <FastForward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Jump to end" arrow>
          <span>
            <IconButton size="small" onClick={() => onSeek(endDate)} disabled={atEnd} sx={smallButtonSx}>
              <SkipNext fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Timeline: activity graph behind the slider, range ends underneath */}
      <Box
        ref={timelineRef}
        flex={1}
        minWidth={120}
        position="relative"
        onMouseMove={handleTimelineMove}
        onMouseLeave={() => setHover(null)}
        sx={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
      >
        {/* Hover: line through the graph + label with the 10-minute period and its target count */}
        {hover && !dragging && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                height: 24,
                left: hover.x,
                width: '1px',
                backgroundColor: alpha('#fff', 0.7),
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '100%',
                mb: 1,
                // keep the label inside the timeline at both ends
                left: Math.min(Math.max(hover.x, 70), Math.max(hover.width - 70, 70)),
                transform: 'translateX(-50%)',
                px: 1,
                py: 0.4,
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                fontSize: '0.72rem',
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.85)',
                border: `1px solid ${alpha(ACCENT, 0.45)}`,
                boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
                textShadow: 'none',
              }}
            >
              <Box component="span" sx={{ color: ACCENT, fontWeight: 800 }}>
                {hover.count.toLocaleString()} targets
              </Box>
              <Box component="span" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {' · '}{dayjs(hover.from).format('MMM D, HH:mm')}–{dayjs(hover.from + BUCKET_MS).format('HH:mm')}
              </Box>
            </Box>
          </>
        )}

        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 2,
            height: 22,
            opacity: 0.45,
            pointerEvents: 'none',   // the slider gets the clicks
          }}
        >
          <GraphCell data={targetCounts} float={0} />
        </Box>

        <Slider
          size="small"
          min={start}
          max={end}
          value={Math.min(Math.max(now, start), end)}
          onChange={(_, v) => { setDragging(true); onScrub(dayjs(v)) }}
          onChangeCommitted={(_, v) => { setDragging(false); onSeek(dayjs(v)) }}
          valueLabelDisplay={dragging ? 'auto' : 'off'}
          valueLabelFormat={(v) => dayjs(v).format('MMM D, HH:mm:ss')}
          aria-label="Replay time"
          sx={{
            mt: 1.5,
            py: '8px !important',
            color: ACCENT,
            height: 3,
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
              backgroundColor: '#fff',
              border: `2px solid ${ACCENT}`,
              boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.2)}`,
              '&:hover, &.Mui-focusVisible, &.Mui-active': { boxShadow: `0 0 0 6px ${alpha(ACCENT, 0.25)}` },
            },
            '& .MuiSlider-track': { border: 'none' },
            '& .MuiSlider-rail': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.22)' },
            '& .MuiSlider-valueLabel': {
              backgroundColor: 'rgba(0,0,0,0.85)',
              border: `1px solid ${alpha(ACCENT, 0.4)}`,
              fontSize: '0.7rem',
            },
          }}
        />

        <Box
          display="flex"
          justifyContent="space-between"
          sx={{ mt: -0.75, color: 'rgba(255,255,255,0.75)', fontSize: '0.65rem', lineHeight: 1.2, textShadow: overlayTextShadow }}
        >
          <span>{startDate.format('MMM D, HH:mm')}</span>
          <span>{endDate.format('MMM D, HH:mm')}</span>
        </Box>
      </Box>

      {/* Speed */}
      <Tooltip title="Playback speed" arrow>
        <Button
          size="small"
          onClick={(e) => setSpeedAnchor(e.currentTarget)}
          sx={{
            minWidth: 44,
            height: 28,
            px: 1,
            flexShrink: 0,
            borderRadius: '8px',
            fontWeight: 800,
            fontSize: '0.75rem',
            color: ACCENT,
            border: `1px solid ${alpha(ACCENT, 0.5)}`,
            backgroundColor: alpha(ACCENT, 0.1),
            '&:hover': { backgroundColor: alpha(ACCENT, 0.2) },
          }}
        >
          {playSpeed}x
        </Button>
      </Tooltip>
      <Menu
        anchorEl={speedAnchor}
        open={Boolean(speedAnchor)}
        onClose={() => setSpeedAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { ...darkPaperSx, minWidth: 80 } } }}
      >
        {SPEEDS.map((s) => (
          <MenuItem
            key={s}
            selected={s === playSpeed}
            onClick={() => { onSpeed(s); setSpeedAnchor(null) }}
            sx={{
              justifyContent: 'center',
              fontSize: '0.8rem',
              fontWeight: 700,
              '&.Mui-selected, &.Mui-selected:hover': { color: ACCENT, backgroundColor: alpha(ACCENT, 0.15) },
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
            }}
          >
            {s}x
          </MenuItem>
        ))}
      </Menu>

      {/* Date range */}
      <Tooltip title={`Replay range · ${formatRange(end - start)}`} arrow>
        <IconButton size="small" onClick={(e) => setRangeAnchor(e.currentTarget)} sx={{ ...smallButtonSx, flexShrink: 0 }}>
          <CalendarMonth fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(rangeAnchor)}
        anchorEl={rangeAnchor}
        onClose={() => setRangeAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: darkPaperSx } }}
      >
        <Box p={1.5}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.14em', color: ACCENT, mb: 1.25 }}>
            REPLAY RANGE · {formatRange(end - start).toUpperCase()}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" gap={1.5} flexWrap="wrap">
              <DateTimePicker
                label="From"
                value={startDate}
                maxDateTime={endDate}
                onChange={onStartChange}
                ampm={false}
                slotProps={{ popper: popperProps, textField: { size: 'small', sx: pickerFieldSx } }}
              />
              <DateTimePicker
                label="To"
                value={endDate}
                minDateTime={startDate}
                onChange={onEndChange}
                ampm={false}
                slotProps={{ popper: popperProps, textField: { size: 'small', sx: pickerFieldSx } }}
              />
            </Box>
          </LocalizationProvider>
        </Box>
      </Popover>
    </Box>
  )
}

export default ReplayControls
